import { diag, since } from "./diag";

/**
 * Turns a picked photo into a JPEG of at most MAX_SIDE px, ready to upload.
 *
 * This used to go through browser-image-compression, whose web worker loads
 * the library from a CDN at run time: on a slow connection that could hang,
 * and a photo it couldn't read (a Samsung HEIC) came back unchanged, showed
 * as a broken image and never finished uploading on Android. Now every step
 * has a time limit, and a photo that can't be turned into a JPEG is rejected
 * with a message the person can act on instead of spinning forever.
 */

const MAX_SIDE = 1920;
const JPEG_QUALITY = 0.85;
const DECODE_TIMEOUT_MS = 20_000;
const HEIC_TIMEOUT_MS = 60_000;
// A GIF is kept as it is (it may be animated), up to this size.
const MAX_GIF_BYTES = 15 * 1024 * 1024;

export class ImagePrepareError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ImagePrepareError";
  }
}

// Files this module made, with their longest side limit: preparing one again
// returns it unchanged (screens prepare a photo for the preview, then the
// upload used to do it again).
const prepared = new WeakMap<Blob, number>();

// One preparation per picked file and size, even when asked twice at once.
const inflight = new WeakMap<Blob, Map<number, Promise<File>>>();

type Kind = "jpeg" | "png" | "gif" | "webp" | "animated-webp" | "heic" | "avif" | "tiff" | "unknown" | "unreadable";

function withTimeout<T>(promise: Promise<T>, ms: number, what: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${what} timed out after ${ms} ms`)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

/** The real format, from the first bytes (names and types from pickers lie). */
async function sniff(file: Blob): Promise<Kind> {
  let bytes: Uint8Array;
  try {
    bytes = new Uint8Array(await withTimeout(file.slice(0, 32).arrayBuffer(), DECODE_TIMEOUT_MS, "read"));
  } catch (error) {
    diag("image read failed", {
      type: file.type,
      kb: Math.round(file.size / 1024),
      error: `${(error as Error)?.name}: ${String((error as Error)?.message ?? error).slice(0, 80)}`,
    });
    return "unreadable";
  }
  const ascii = (from: number, to: number) => String.fromCharCode(...bytes.slice(from, to));
  if (bytes[0] === 0xff && bytes[1] === 0xd8) return "jpeg";
  if (bytes[0] === 0x89 && ascii(1, 4) === "PNG") return "png";
  if (ascii(0, 3) === "GIF") return "gif";
  if (ascii(0, 4) === "RIFF" && ascii(8, 12) === "WEBP") {
    // Extended WebP with the animation flag set.
    return ascii(12, 16) === "VP8X" && (bytes[20] & 0x02) !== 0 ? "animated-webp" : "webp";
  }
  // TIFF (scanners, some cameras' RAW files such as DNG start the same way).
  if (ascii(0, 4) === "II*\0" || ascii(0, 4) === "MM\0*") return "tiff";
  if (ascii(4, 8) === "ftyp") {
    const brand = ascii(8, 12);
    if (brand === "avif" || brand === "avis") return "avif";
    if (["heic", "heix", "hevc", "hevx", "heim", "heis", "mif1", "msf1"].includes(brand)) return "heic";
  }
  return "unknown";
}

interface Decoded {
  source: CanvasImageSource;
  width: number;
  height: number;
  release: () => void;
}

async function decodeWithImageBitmap(blob: Blob): Promise<Decoded> {
  const bitmap = await createImageBitmap(blob, { imageOrientation: "from-image" });
  return { source: bitmap, width: bitmap.width, height: bitmap.height, release: () => bitmap.close() };
}

async function decodeWithImg(blob: Blob): Promise<Decoded> {
  const url = URL.createObjectURL(blob);
  try {
    const img = new Image();
    img.decoding = "async";
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error("image could not be decoded"));
      img.src = url;
    });
    if (!img.naturalWidth || !img.naturalHeight) throw new Error("image has no size");
    return {
      source: img,
      width: img.naturalWidth,
      height: img.naturalHeight,
      release: () => URL.revokeObjectURL(url),
    };
  } catch (error) {
    URL.revokeObjectURL(url);
    throw error;
  }
}

/** Decodes with the browser (createImageBitmap, then <img>), or throws. */
async function decode(blob: Blob): Promise<Decoded> {
  if (typeof createImageBitmap === "function") {
    try {
      return await withTimeout(decodeWithImageBitmap(blob), DECODE_TIMEOUT_MS, "createImageBitmap");
    } catch {
      // Older WebViews: fall back to <img>.
    }
  }
  return withTimeout(decodeWithImg(blob), DECODE_TIMEOUT_MS, "img decode");
}

async function toJpeg(image: Decoded, maxSide: number): Promise<Blob> {
  const scale = Math.min(1, maxSide / Math.max(image.width, image.height));
  const width = Math.max(1, Math.round(image.width * scale));
  const height = Math.max(1, Math.round(image.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("no 2d canvas");
  // JPEG has no transparency: transparent PNG areas would turn black.
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(image.source, 0, 0, width, height);
  try {
    return await withTimeout(
      new Promise<Blob>((resolve, reject) =>
        canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("toBlob failed"))), "image/jpeg", JPEG_QUALITY),
      ),
      DECODE_TIMEOUT_MS,
      "toBlob",
    );
  } finally {
    // Frees the canvas memory right away (iOS limits the total).
    canvas.width = 0;
    canvas.height = 0;
  }
}

/** TIFF → pixels in JavaScript, for browsers that can't show TIFF (all but Safari). */
async function decodeTiff(file: Blob): Promise<Decoded> {
  const UTIF = (await import("utif")).default;
  const buffer = await withTimeout(file.arrayBuffer(), DECODE_TIMEOUT_MS, "read");
  const ifds = UTIF.decode(buffer);
  // The largest picture in the file (a DNG also holds small previews).
  const all = ifds.flatMap((ifd) => [ifd, ...(ifd.subIFD ?? [])]);
  const size = (ifd: (typeof all)[number]) => (ifd.t256?.[0] ?? 0) * (ifd.t257?.[0] ?? 0);
  const best = all.reduce((a, b) => (size(b) > size(a) ? b : a), all[0]);
  if (!best) throw new Error("no image in TIFF");
  UTIF.decodeImage(buffer, best, ifds);
  const rgba = UTIF.toRGBA8(best);
  if (!best.width || !best.height) throw new Error("TIFF has no size");
  const pixels = new ImageData(new Uint8ClampedArray(rgba), best.width, best.height);
  const bitmap = await createImageBitmap(pixels);
  return { source: bitmap, width: bitmap.width, height: bitmap.height, release: () => bitmap.close() };
}

/** HEIC → JPEG in JavaScript, for browsers that can't show HEIC (Android, Chrome). */
async function heicToJpeg(file: Blob): Promise<Blob> {
  const heic2any = (await import("heic2any")).default;
  const result = await withTimeout(
    heic2any({ blob: file, toType: "image/jpeg", quality: 0.9 }),
    HEIC_TIMEOUT_MS,
    "heic2any",
  );
  return Array.isArray(result) ? result[0] : result;
}

function jpegName(name: string): string {
  const base = (name || "slika").replace(/\.[^/.]+$/, "") || "slika";
  return `${base}.jpg`;
}

function finish(blob: Blob, name: string, maxSide: number, type = "image/jpeg"): File {
  const file = new File([blob], type === "image/jpeg" ? jpegName(name) : name, {
    type,
    lastModified: Date.now(),
  });
  prepared.set(file, maxSide);
  return file;
}

async function prepare(file: File, maxSide: number): Promise<File> {
  const started = performance.now();
  // An empty file is a picture still in the cloud (Google Photos, iCloud).
  const kind = file.size === 0 ? "unreadable" : await sniff(file);

  if (kind === "gif" && file.size <= MAX_GIF_BYTES) return finish(file, file.name || "slika.gif", maxSide, "image/gif");
  if (kind === "animated-webp" && file.size <= MAX_GIF_BYTES) {
    return finish(file, file.name || "slika.webp", maxSide, "image/webp");
  }

  let source: Blob = file;
  let image: Decoded | null = null;
  try {
    if (kind === "unreadable") throw new Error("unreadable");
    image = await decode(source);
  } catch (error) {
    if (kind !== "heic" && kind !== "tiff" && kind !== "unknown" && kind !== "unreadable") {
      diag("image decode failed", { kind, type: file.type, kb: Math.round(file.size / 1024), message: String(error).slice(0, 100) });
    }
  }

  if (!image && kind === "tiff") {
    try {
      image = await withTimeout(decodeTiff(file), HEIC_TIMEOUT_MS, "tiff decode");
    } catch (error) {
      diag("tiff decode failed", { kb: Math.round(file.size / 1024), message: String(error).slice(0, 100) });
    }
  }

  // HEIC on a browser that can't show it (Android, Chrome, Firefox).
  if (!image && (kind === "heic" || /\.hei[cf]$/i.test(file.name) || /hei[cf]/i.test(file.type))) {
    try {
      source = await heicToJpeg(file);
      image = await decode(source);
    } catch (error) {
      diag("heic convert failed", { kb: Math.round(file.size / 1024), message: String(error).slice(0, 100) });
    }
  }

  if (!image) {
    throw new ImagePrepareError(
      kind === "unreadable"
        ? "Slika se ne može pročitati s uređaja. Ako je u oblaku (npr. Google Photos), prvo je preuzmite na uređaj, pa pokušajte ponovno."
        : kind === "heic"
        ? "Ova slika je u HEIC formatu koji se nije mogao pretvoriti. Odaberite drugu sliku ili je spremite kao JPG."
        : "Ova datoteka se ne može otvoriti kao slika. Odaberite drugu sliku (JPG ili PNG).",
    );
  }

  try {
    const small = image.width <= maxSide && image.height <= maxSide;
    // Already a small JPEG: re-encoding would only lose quality.
    if (kind === "jpeg" && small && file.size <= 1.5 * 1024 * 1024) {
      return finish(file, file.name, maxSide);
    }
    const jpeg = await toJpeg(image, maxSide);
    diag("image prepared", {
      kind,
      from: `${image.width}x${image.height}`,
      kb: Math.round(file.size / 1024),
      toKb: Math.round(jpeg.size / 1024),
      ms: since(started),
    });
    return finish(jpeg, file.name, maxSide);
  } catch (error) {
    // Couldn't re-encode, but the browser can show the original: use it.
    if (kind === "jpeg" || kind === "png" || kind === "webp" || kind === "animated-webp") {
      diag("image re-encode failed, using original", { kind, message: String(error).slice(0, 100) });
      return finish(file, file.name, maxSide, file.type || `image/${kind}`);
    }
    throw new ImagePrepareError("Slika se nije mogla pripremiti. Pokušajte ponovno ili odaberite drugu sliku.");
  } finally {
    image.release();
  }
}

/**
 * A JPEG version of a picked photo (longest side at most `maxSide` px,
 * correctly rotated). Throws ImagePrepareError, with a message to show, if
 * the photo can't be read.
 */
export function prepareImage(file: File, maxSide = MAX_SIDE): Promise<File> {
  const preparedAt = prepared.get(file);
  if (preparedAt !== undefined && preparedAt <= maxSide) return Promise.resolve(file);
  let bySize = inflight.get(file);
  if (!bySize) {
    bySize = new Map();
    inflight.set(file, bySize);
  }
  let pending = bySize.get(maxSide);
  if (!pending) {
    pending = prepare(file, maxSide);
    bySize.set(maxSide, pending);
  }
  return pending;
}

/** The message to show for an error from prepareImage, or a general one. */
export function imageErrorMessage(error: unknown): string {
  return error instanceof ImagePrepareError
    ? error.message
    : "Slika se nije mogla pripremiti. Pokušajte ponovno ili odaberite drugu sliku.";
}

// Local emulator builds only: lets the QA scripts run this on the devices.
if (import.meta.env.VITE_USE_EMULATORS === "true") {
  (window as unknown as { __nnPrepare: unknown }).__nnPrepare = { prepareImage };
}
